import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useIsMobile } from 'core/hooks/Layout/useMediaQuery';
import {
  CommunicatorMobileStep,
  CommunicatorTab,
  deriveMobileStep,
  encode,
  ICommunicatorDeepLinkState,
  isSelectionChange,
  MOBILE_STEP_PARAM,
  normalize,
  parentMobileStep,
  parse,
  parseMobileStep,
} from 'core/utils/communicatorDeepLink';

/** Метка записи истории, положенной шагом телефона (ТЗ 0121): «Назад» на
 *  странице по ней знает, что можно честно вернуться `goBack()`. */
interface ICommunicatorHistoryState {
  communicatorStep?: boolean;
}

/**
 * Thin React wrapper over `core/utils/communicatorDeepLink`: reads the
 * communicator view state from `location.search` and commits transitions via
 * `history.replace` (tab/selection switches should not pollute the back
 * stack — same convention as the dashboard and the group card).
 */
export const useCommunicatorDeepLink = () => {
  const history = useHistory();
  const location = useLocation();

  const state = useMemo(() => parse(location.search), [location.search]);
  // ТЗ 0121: шаг стека на телефоне — `?m=`, вне состояния диплинка.
  const rawStep = useMemo(
    () => new URLSearchParams(location.search).get(MOBILE_STEP_PARAM) || '',
    [location.search],
  );
  const mobileStep = useMemo(() => parseMobileStep(location.search, state), [
    location.search,
    state,
  ]);

  const build = useCallback(
    (next: ICommunicatorDeepLinkState, step: string): string => {
      const params = encode(normalize(next));
      if (step) params.set(MOBILE_STEP_PARAM, step);
      return params.toString();
    },
    [],
  );

  const isMobile = useIsMobile();

  // Обычная смена выбора — replace: выбор не засоряет историю (как было).
  // Шаг телефона: на компьютере `m` в адресе не нужен — его нет; на телефоне
  // смена группы / собеседника ставит шаг по НОВОМУ выбору (старый `m` мог
  // указывать на экран, которого у нового выбора нет), прочее — прежний шаг.
  const commit = useCallback(
    (next: ICommunicatorDeepLinkState) => {
      const normalized = normalize(next);
      let step = '';
      if (isMobile) {
        step = isSelectionChange(state, normalized)
          ? deriveMobileStep(normalized)
          : rawStep;
      }
      history.replace({ ...location, search: build(normalized, step) });
    },
    [history, location, build, rawStep, isMobile, state],
  );

  /**
   * ТЗ 0121: шаг вглубь на телефоне — НОВАЯ запись истории (push) с выбором
   * и шагом сразу: системный «Назад» и смахивание возвращают на шаг выше.
   */
  const pushStep = useCallback(
    (
      step: CommunicatorMobileStep,
      patch: Partial<ICommunicatorDeepLinkState> = {},
    ) => {
      const historyState: ICommunicatorHistoryState = {
        communicatorStep: true,
      };
      history.push({
        pathname: location.pathname,
        search: build({ ...state, ...patch }, step),
        state: historyState,
      });
    },
    [history, location.pathname, build, state],
  );

  /**
   * «Назад» на странице: если текущую запись положил `pushStep` — честный
   * `goBack()` (как системный), иначе (открыли по ссылке) — replace на шаг
   * выше, чтобы не вывести человека из приложения.
   */
  const stepBack = useCallback(() => {
    const historyState = location.state as ICommunicatorHistoryState | null;
    if (historyState?.communicatorStep) {
      history.goBack();
      return;
    }
    history.replace({
      ...location,
      search: build(state, parentMobileStep(mobileStep)),
    });
  }, [history, location, build, state, mobileStep]);

  /** Select a group ('' deselects). Keeps tab and search, resets context. */
  const setGroup = useCallback(
    (groupId: string) =>
      commit({
        ...state,
        group: groupId,
        student: '',
        teacher: '',
        ctx: '',
        conv: '',
      }),
    [commit, state],
  );

  /** Select a student of the current group ('' returns to the whole group). */
  const setStudent = useCallback(
    (studentId: string) =>
      commit({ ...state, student: studentId, teacher: '', ctx: '', conv: '' }),
    [commit, state],
  );

  /** Выбрать преподавателя группы (ТЗ 0032); '' — назад ко «всей группе». */
  const setTeacher = useCallback(
    (teacherId: string) =>
      commit({ ...state, teacher: teacherId, student: '', ctx: '', conv: '' }),
    [commit, state],
  );

  /** Select the pinned "group channels" context. */
  const setChannelsCtx = useCallback(
    () =>
      commit({ ...state, student: '', teacher: '', ctx: 'channels', conv: '' }),
    [commit, state],
  );

  const setTab = useCallback(
    (tab: CommunicatorTab) => commit({ ...state, tab, conv: '' }),
    [commit, state],
  );

  /** Preselect a conversation inside the current context (optionally + tab). */
  const openConversation = useCallback(
    (conv: string, patch?: Partial<ICommunicatorDeepLinkState>) =>
      commit({ ...state, ...patch, conv }),
    [commit, state],
  );

  const setQuery = useCallback((query: string) => commit({ ...state, query }), [
    commit,
    state,
  ]);

  return {
    state,
    mobileStep,
    pushStep,
    stepBack,
    setGroup,
    setStudent,
    setTeacher,
    setChannelsCtx,
    setTab,
    openConversation,
    setQuery,
  };
};
